import React from 'react';
import { Link } from 'gatsby';
import styled from 'styled-components';
import Branded from '../components/Branded';

const Section = styled.section`
  position: relative;
  overflow: hidden;
  min-height: 520px;
  display: flex;
  align-items: center;

  @media (max-width: 768px) {
    min-height: 420px;
  }
`;

const BgImage = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to right,
    rgba(19, 120, 229, 0.92) 0%,
    rgba(19, 120, 229, 0.65) 60%,
    rgba(19, 120, 229, 0.5) 100%
  );
  z-index: 1;
`;

const Glow = styled.div`
  position: absolute;
  left: -200px;
  top: -100px;
  width: 620px;
  height: 620px;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(240, 128, 32, 0.18) 0%,
    transparent 70%
  );
  z-index: 1;
  pointer-events: none;

  @media (max-width: 768px) {
    left: -300px;
    top: -200px;
  }
`;

const Container = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1400px;
  margin: 0 auto;
  padding: 100px 48px;
  width: 100%;

  @media (max-width: 768px) {
    padding: 72px 24px;
  }
`;

const Title = styled.h2`
  font-size: 3.4rem;
  font-weight: 600;
  color: #fff;
  line-height: 1.15;
  letter-spacing: -0.01em;
  max-width: 720px;
  margin-bottom: 48px;

  /* Blue on blue would vanish — keep the orange half, whiten the blue half */
  .brand-space {
    color: #fff;
  }

  @media (max-width: 1024px) {
    font-size: 2.6rem;
  }

  @media (max-width: 768px) {
    font-size: 1.9rem;
    margin-bottom: 36px;
  }
`;

const CtaButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 14px;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.04em;
  color: #fff;
  background: transparent;
  border: 1.5px solid rgba(255, 255, 255, 0.5);
  border-radius: 999px;
  padding: 18px 36px 18px 28px;
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  position: relative;
  overflow: hidden;
  z-index: 1;

  .arrow-circle {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.15);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
  }

  svg {
    width: 12px;
    height: 12px;
    stroke: #fff;
    fill: none;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
    transition: transform 0.3s ease;
  }

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: #f08020;
    border-radius: 999px;
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.45s cubic-bezier(0.16, 1, 0.3, 1);
    z-index: -1;
  }

  &:hover {
    border-color: #f08020;
    color: #fff;
    padding-left: 14px;

    &::before {
      transform: scaleX(1);
    }

    .arrow-circle {
      background: rgba(255, 255, 255, 0.25);
    }

    svg {
      stroke: #fff;
      transform: translateX(2px);
    }
  }
`;

export default function CtaBanner({
  title,
  ctaLabel,
  ctaLink,
  backgroundImage,
}) {
  if (!title && !ctaLabel) return null;

  return (
    <Section>
      {backgroundImage?.asset?.url && (
        <BgImage>
          <img src={backgroundImage.asset.url + '?w=1920&auto=format'} alt="" />
        </BgImage>
      )}
      <Overlay />
      <Glow />

      <Container>
        <Branded as={Title} text={title} />
        {ctaLabel && (
          <CtaButton to={ctaLink || '/contact'}>
            <span className="arrow-circle">
              <svg viewBox="0 0 24 24">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </span>
            {ctaLabel}
          </CtaButton>
        )}
      </Container>
    </Section>
  );
}
